import React, { useEffect, useState } from "react";
import { Pencil, Trash2, Plus, Calendar, FileText, Image as ImageIcon } from "lucide-react";
import Sidebar from "../../../component/includes/sidebar";
import {
  CreateFacilityData,
  ServiceFacilityContent,
  facilityService,
  UpdateFacilityData,
} from "../../../services/facility";

const FacilityManagement = () => {
  const [facilities, setFacilities] = useState<ServiceFacilityContent[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [editingFacility, setEditingFacility] = useState<ServiceFacilityContent | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);

  const [formData, setFormData] = useState<CreateFacilityData>({
    title: "",
    description: "",
  });

  // Fungsi untuk menghapus tag HTML dari description
  const stripHtmlTags = (html: string): string => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || "";
  };

  useEffect(() => {
    fetchFacilities();
  }, []);

  const fetchFacilities = async () => {
    setLoading(true);
    try {
      const response = await facilityService.getFacilityContent();
      setFacilities(Array.isArray(response) ? response : []);
    } catch (err: any) {
      setError(err?.error || err?.message || "Failed to fetch facilities");
      setIsErrorModalOpen(true);
      console.error("Error fetching facilities:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPreviewUrl(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleCreateFacility = async () => {
    if (!formData.title.trim() || !formData.description.trim()) {
      setError("Title and description cannot be empty");
      setIsErrorModalOpen(true);
      return;
    }

    try {
      await facilityService.createFacilityContent({
        ...formData,
        image: selectedFile || undefined,
      });
      await fetchFacilities();
      setSuccessMessage("Facility created successfully");
      setIsSuccessModalOpen(true);
      resetForm();
    } catch (err: any) {
      setError(err?.error || err?.message || "Failed to create facility");
      setIsErrorModalOpen(true);
    }
  };

  const handleUpdateFacility = async () => {
    if (!editingFacility) return;

    try {
      const updateData: UpdateFacilityData = {
        id: editingFacility.id,
        title: formData.title,
        description: formData.description,
        image: selectedFile || undefined,
      };
      await facilityService.updateFacilityContent(updateData);
      await fetchFacilities();
      setSuccessMessage("Facility updated successfully");
      setIsSuccessModalOpen(true);
      resetForm();
    } catch (err: any) {
      setError(err?.error || err?.message || "Failed to update facility");
      setIsErrorModalOpen(true);
    }
  };

  const handleDeleteFacility = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this facility?")) return;

    try {
      await facilityService.deleteFacilityContent(id);
      await fetchFacilities();
      setSuccessMessage("Facility deleted successfully");
      setIsSuccessModalOpen(true);
    } catch (err: any) {
      setError(err?.error || err?.message || "Failed to delete facility");
      setIsErrorModalOpen(true);
    }
  };

  const resetForm = () => {
    setFormData({ title: "", description: "" });
    setSelectedFile(null);
    setPreviewUrl(null);
    setEditingFacility(null);
    setIsModalOpen(false);
  };

  const filteredFacilities = facilities.filter((item) =>
    item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    stripHtmlTags(item.description).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalFacilities = filteredFacilities.length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar  />

      <div className="flex-1 flex flex-col">
<div className="flex-1 p-4 md:p-6 mt-14">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="bg-white rounded-3xl shadow-sm p-6 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center">
                  <span className="text-white text-3xl">🏢</span>
                </div>
                <div>
                  <h1 className="text-3xl font-semibold text-gray-900">Facility Management</h1>
                  <p className="text-gray-500 text-sm mt-1">Manage your facility content</p>
                </div>
              </div>

              <button
                onClick={() => {
                  resetForm();
                  setIsModalOpen(true);
                }}
                className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-2xl font-medium transition-all active:scale-95"
              >
                <Plus className="w-5 h-5" />
                Add New Facility
              </button>
            </div>

            {/* Search Bar */}
            <div className="mb-8">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search facilities by title or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-white border border-gray-200 rounded-2xl py-4 pl-12 pr-6 focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-400 transition-all"
                />
                <div className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400">🔍</div>
              </div>
            </div>

            {/* Table / Empty State */}
            <div className="bg-white rounded-3xl shadow-sm overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="px-8 py-5 text-left font-medium text-gray-600 w-20">ID</th>
                    <th className="px-8 py-5 text-left font-medium text-gray-600">IMAGE</th>
                    <th className="px-8 py-5 text-left font-medium text-gray-600">TITLE</th>
                    <th className="px-8 py-5 text-left font-medium text-gray-600">DESCRIPTION</th>
                    <th className="px-8 py-5 text-left font-medium text-gray-600 w-44">CREATED AT</th>
                    <th className="px-8 py-5 text-left font-medium text-gray-600 w-44">UPDATED AT</th>
                    <th className="px-8 py-5 text-right font-medium text-gray-600 w-32">ACTIONS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredFacilities.length > 0 ? (
                    filteredFacilities.map((item, index) => (
                      <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-8 py-6">
                          <div className="w-9 h-9 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold text-lg">
                            {index + 1}
                          </div>
                        </td>
                        <td className="px-8 py-6">
                          {item.image && (
                            <img
                              src={`${process.env.REACT_APP_API_URL || ''}${item.image}`}
                              alt={item.title}
                              className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                            />
                          )}
                        </td>
                        <td className="px-8 py-6 font-medium text-gray-800">{item.title}</td>
                        <td className="px-8 py-6 text-gray-700 max-w-md">
                          {stripHtmlTags(item.description).length > 150
                            ? stripHtmlTags(item.description).substring(0, 150) + "..."
                            : stripHtmlTags(item.description)}
                        </td>
                        <td className="px-8 py-6 text-gray-500">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            {new Date(item.created_at).toLocaleDateString('id-ID', {
                              year: 'numeric', month: 'short', day: 'numeric'
                            })}
                          </div>
                        </td>
                        <td className="px-8 py-6 text-gray-500">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            {new Date(item.updated_at).toLocaleDateString('id-ID', {
                              year: 'numeric', month: 'short', day: 'numeric'
                            })}
                          </div>
                        </td>
                        <td className="px-8 py-6">
                          <div className="flex items-center justify-end gap-4">
                            <button
                              onClick={() => {
                                setEditingFacility(item);
                                setFormData({
                                  title: item.title,
                                  description: item.description,
                                });
                                setPreviewUrl(typeof item.image === "string" ? item.image : null);
                                setIsModalOpen(true);
                              }}
                              className="p-3 text-blue-500 hover:bg-blue-50 rounded-2xl transition-all hover:scale-110"
                            >
                              <Pencil className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDeleteFacility(item.id)}
                              className="p-3 text-red-500 hover:bg-red-50 rounded-2xl transition-all hover:scale-110"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="py-20">
                        <div className="flex flex-col items-center justify-center text-center">
                          <ImageIcon className="w-16 h-16 text-gray-300 mb-4" />
                          <p className="text-gray-500 text-lg font-medium">No facilities found</p>
                          <p className="text-gray-400 text-sm mt-1">
                            Start by adding your first facility
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Total Facilities Footer */}
            <div className="bg-white rounded-3xl shadow-sm mt-6 p-6 text-center">
              <p className="text-gray-600 font-medium">
                Total Facilities: <span className="text-gray-900">{totalFacilities}</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Add/Edit */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-semibold mb-6">
              {editingFacility ? "Edit Facility" : "Add New Facility"}
            </h2>

            <div className="space-y-6">
              <input
                type="text"
                placeholder="Facility Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full border border-gray-300 rounded-2xl px-6 py-4 focus:outline-none focus:border-purple-500"
              />

              <textarea
                placeholder="Facility Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={6}
                className="w-full border border-gray-300 rounded-2xl px-6 py-4 focus:outline-none focus:border-purple-500"
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Facility Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="w-full border border-gray-300 rounded-2xl px-4 py-3"
                />
                {previewUrl && (
                  <img 
                    src={previewUrl} 
                    alt="Preview" 
                    className="mt-4 max-h-60 object-cover rounded-lg border" 
                  />
                )}
              </div>
            </div>

            <div className="flex gap-4 mt-10">
              <button
                onClick={() => setIsModalOpen(false)}
                className="flex-1 py-4 border border-gray-300 rounded-2xl font-medium hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={editingFacility ? handleUpdateFacility : handleCreateFacility}
                className="flex-1 py-4 bg-purple-600 hover:bg-purple-700 text-white rounded-2xl font-medium transition-all"
              >
                {editingFacility ? "Update Facility" : "Create Facility"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {isSuccessModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/60 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center">
            <h2 className="text-2xl font-bold text-green-600 mb-4">Success</h2>
            <p className="text-gray-700 mb-6">{successMessage}</p>
            <button 
              onClick={() => setIsSuccessModalOpen(false)} 
              className="bg-green-500 text-white px-10 py-3 rounded-2xl hover:bg-green-600"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {isErrorModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/60 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
            <p className="text-gray-700 mb-6">{error}</p>
            <button 
              onClick={() => setIsErrorModalOpen(false)} 
              className="bg-red-500 text-white px-10 py-3 rounded-2xl hover:bg-red-600"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FacilityManagement;